import React, { useState } from "react";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import {
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  Divider,
} from "@mui/material";
import { Visibility, PlayArrow, Delete, Memory, Speed, Group, EmojiEvents, MonetizationOn } from "@mui/icons-material";
import RunAndTest from "components/play-ground/Run";
import { useDeleteClassicLevelMutation } from "lib/store/service/classic";
import toast from "react-hot-toast";

// Render View Details Action Cell & Comprehensive Classic Level Details Modal
const RenderClassicActions = ({ level, onRefetch }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [openRunModel, setOpenRunModel] = useState(false);
  const [deleteClassicLevel] = useDeleteClassicLevelMutation();

  const nodeCount = level.nodesCount || level.nodes?.length || 0;
  const diff = level.difficulty || (nodeCount <= 4 ? "Easy" : nodeCount <= 7 ? "Medium" : "Hard");
  const diffColor = diff === "Easy" ? "success" : diff === "Hard" ? "error" : "warning";

  const plays = level.playsCount !== undefined ? level.playsCount : (level.playCount || 0);
  const completions = level.completionsCount !== undefined ? level.completionsCount : (level.completionCount || 0);
  const completionRate = plays > 0 ? `${((completions / plays) * 100).toFixed(1)}%` : "100.0%";

  const gateTypes = [
    ...new Set(
      (level.nodes || [])
        .filter((n) => n.type === "gate" && n.data?.label)
        .map((n) => n.data.label)
    ),
  ];
  const displayGates = gateTypes.length > 0 ? gateTypes : ["BASIC"];

  const handleConfirmDelete = async (lvlId) => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-md w-full bg-slate-900 border border-slate-700 shadow-2xl rounded-2xl p-4 pointer-events-auto text-white`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-rose-500/20 text-rose-500 flex items-center justify-center font-bold text-lg">
              ⚠️
            </div>
            <div>
              <p className="text-sm font-bold text-white">Delete Classic Level?</p>
              <p className="text-xs text-slate-400">
                Are you sure you want to delete <strong className="text-cyan-400">{level.level_slug || level.title}</strong>?
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg"
              onClick={() => toast.remove(t.id)}
            >
              Cancel
            </button>
            <button
              className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg shadow-lg"
              onClick={async () => {
                toast.remove(t.id);
                try {
                  await deleteClassicLevel(lvlId).unwrap();
                  toast.success("✅ Classic Level deleted successfully!");
                  setDetailsOpen(false);
                  if (onRefetch) onRefetch();
                } catch (err) {
                  toast.error("Failed to delete level");
                }
              }}
            >
              Confirm Remove
            </button>
          </div>
        </div>
      ),
      { duration: 5000 }
    );
  };

  return (
    <>
      {/* Table Single View Details Action Button */}
      <Button
        variant="contained"
        size="small"
        startIcon={<Visibility />}
        onClick={() => setDetailsOpen(true)}
        sx={{
          fontSize: "11px",
          fontWeight: "bold",
          backgroundColor: "#06b6d4 !important",
          color: "#ffffff !important",
          borderRadius: "8px",
          px: 2.5,
          boxShadow: "0 0 12px rgba(6, 182, 212, 0.4)",
          "&:hover": {
            backgroundColor: "#0891b2 !important",
            boxShadow: "0 0 18px rgba(6, 182, 212, 0.7)",
          },
        }}
      >
        View Details
      </Button>

      {/* Comprehensive Classic Level Info & Action Modal Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#0f172a",
            color: "#fff",
            borderRadius: "20px",
            border: "1px solid #06b6d4",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #1e293b", pb: 2 }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox display="flex" alignItems="center" gap={1.5}>
              <Chip label={level.level_slug?.toUpperCase() || "CLASSIC_LVL"} color="primary" sx={{ fontWeight: "bold" }} />
              <VuiTypography variant="h5" color="white" fontWeight="bold">
                {level.title || `Level ${level.level_slug?.replace("classic_lvl_", "")}`}
              </VuiTypography>
            </VuiBox>
            <Chip label={diff} color={diffColor} sx={{ fontWeight: "bold" }} />
          </VuiBox>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          {/* Level Play & Win Coins Analytics Grid */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "125px", display: "flex", flexDirection: "column", alignItems: "center", justifyContents: "center" }}>
                <MonetizationOn sx={{ color: "#f59e0b", mb: 0.5, fontSize: 28 }} />
                <VuiTypography variant="caption" color="text">WIN REWARD COINS</VuiTypography>
                <VuiTypography variant="h4" color="warning" fontWeight="bold">
                  🪙 +{level.rewardCoins || 10} Coins
                </VuiTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "125px", display: "flex", flexDirection: "column", alignItems: "center", justifyContents: "center" }}>
                <Group sx={{ color: "#38bdf8", mb: 0.5, fontSize: 28 }} />
                <VuiTypography variant="caption" color="text">PLAYED BY USERS</VuiTypography>
                <VuiTypography variant="h4" color="white" fontWeight="bold">
                  🎮 {plays.toLocaleString()}
                </VuiTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "125px", display: "flex", flexDirection: "column", alignItems: "center", justifyContents: "center" }}>
                <EmojiEvents sx={{ color: "#34d399", mb: 0.5, fontSize: 28 }} />
                <VuiTypography variant="caption" color="text">COMPLETION RATE</VuiTypography>
                <VuiTypography variant="h4" color="success" fontWeight="bold">
                  {completionRate}
                </VuiTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "110px", display: "flex", flexDirection: "column", alignItems: "center", justifyContents: "center" }}>
                <Memory sx={{ color: "#06b6d4", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">CIRCUIT NODES</VuiTypography>
                <VuiTypography variant="h4" color="white" fontWeight="bold">
                  {nodeCount} Nodes
                </VuiTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "110px", display: "flex", flexDirection: "column", alignItems: "center", justifyContents: "center" }}>
                <Speed sx={{ color: "#f59e0b", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">TARGET BEST MOVES</VuiTypography>
                <VuiTypography variant="h4" color="warning" fontWeight="bold">
                  {level.bestMoves || 1} Moves
                </VuiTypography>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ bg: "#334155", my: 2 }} />

          {/* Gate Types Included */}
          <VuiBox mb={2}>
            <VuiTypography variant="button" color="white" fontWeight="bold" mb={1} display="block">
              🧩 Gate Types Included
            </VuiTypography>
            <VuiBox display="flex" gap={1}>
              {displayGates.map((gate, idx) => (
                <Chip
                  key={idx}
                  label={gate}
                  size="small"
                  sx={{ backgroundColor: "#06b6d4 !important", color: "#fff !important", fontWeight: "bold" }}
                />
              ))}
            </VuiBox>
          </VuiBox>
        </DialogContent>

        {/* Modal Action Buttons: Play Level & Delete */}
        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button
            onClick={() => {
              setDetailsOpen(false);
              setOpenRunModel(true);
            }}
            variant="contained"
            startIcon={<PlayArrow />}
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#10b981 !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
              py: 1,
              boxShadow: "0 0 14px rgba(16, 185, 129, 0.4)",
            }}
          >
            Play Level
          </Button>

          <Button
            onClick={() => handleConfirmDelete(level._id)}
            variant="contained"
            startIcon={<Delete />}
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#f43f5e !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
              py: 1,
              boxShadow: "0 0 14px rgba(244, 63, 94, 0.4)",
            }}
          >
            Delete Level
          </Button>

          <Button onClick={() => setDetailsOpen(false)} sx={{ color: "#94a3b8" }}>
            Close Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* ReactFlow Interactive Circuit Player Modal */}
      {openRunModel && (
        <RunAndTest
          data={{
            levelData: { nodes: level.nodes || [], edges: level.edges || [] },
            outputId: level.completeNodeId || "4",
          }}
          onClose={() => setOpenRunModel(false)}
        />
      )}
    </>
  );
};

// Generate Table Data for Classic Levels
export const getTableData = (apiResponse, onRefetch) => {
  const rawLevels = apiResponse?.response?.data?.levels || apiResponse?.data?.levels || [];
  const levels = [...rawLevels].sort((a, b) => {
    const numA = parseInt(a.level_slug?.match(/\d+/)?.[0] || 0, 10);
    const numB = parseInt(b.level_slug?.match(/\d+/)?.[0] || 0, 10);
    return numA - numB;
  });

  const rows = levels.map((level) => {
    const nodeCount = level.nodesCount || level.nodes?.length || 0;
    const diff = level.difficulty || (nodeCount <= 4 ? "Easy" : nodeCount <= 7 ? "Medium" : "Hard");
    const chipColor = diff === "Easy" ? "success" : diff === "Hard" ? "error" : "warning";
    const levelNum = level.level_slug?.match(/\d+/)?.[0] || "";

    return {
      slug: (
        <VuiTypography variant="caption" fontWeight="bold" sx={{ color: "#38bdf8 !important" }}>
          {level.level_slug?.toUpperCase() || "CLASSIC_LVL"}
        </VuiTypography>
      ),
      title: (
        <VuiTypography variant="button" color="white" fontWeight="bold">
          {level.title || level.name || (levelNum ? `Level ${levelNum}` : "Level")}
        </VuiTypography>
      ),
      difficulty: (
        <Chip
          label={diff}
          color={chipColor}
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
    nodes: (
      <VuiTypography variant="caption" color="white" fontWeight="bold">
        {level.nodesCount || level.nodes?.length || 5} Nodes | {level.bestMoves || 2} Moves
      </VuiTypography>
    ),
    coins: (
      <VuiTypography variant="button" color="warning" fontWeight="bold">
        🪙 +{level.rewardCoins || 10} Coins
      </VuiTypography>
    ),
    status: (
      <Chip label="ACTIVE CAMPAIGN" color="success" size="small" sx={{ fontWeight: "bold" }} />
    ),
    action: <RenderClassicActions level={level} onRefetch={onRefetch} />,
  }});

  return {
    columns: [
      { name: "slug", label: "LEVEL SLUG", align: "left" },
      { name: "title", label: "TITLE", align: "left" },
      { name: "difficulty", label: "DIFFICULTY", align: "center" },
      { name: "nodes", label: "TARGET NODES & MOVES", align: "center" },
      { name: "coins", label: "WIN REWARD COINS", align: "center" },
      { name: "status", label: "STATUS", align: "center" },
      { name: "action", label: "ACTIONS", align: "center" },
    ],
    rows,
  };
};
